import { LinkIcon } from '@heroicons/react/20/solid';
import { useAsyncList } from '@react-stately/data';
import { Key, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { format } from 'date-fns';
import { ExpectedTag } from 'src/shared/components';
import { BulkOrdering } from 'src/shared/types/bulkOrdering';
import { formatMoney } from 'src/shared/utils/formatMoney';
import isValidDate from 'src/shared/utils/isValidDate';

import {
  cn,
  SortDescriptor,
  Spinner,
  TableColumn
} from 'src/shared/components/ui';
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  Typography
} from 'src/shared/components/ui';

import bulkOrderingsTableColumns from '../constants/bulkOrderingsTableColumns';
import {
  CAVELA_DIRECT_STAGE_LABELS,
  STAGE_LABELS
} from '../constants/bulkOrderingTimeline';
import getDeliveredDate from '../utils/getDeliveredDate';

interface OrdersTableProps {
  orders: BulkOrdering[];
  isLoading?: boolean;
  isProductTab?: boolean;
}

const OrdersTable: React.FC<OrdersTableProps> = ({
  orders,
  isLoading = false,
  isProductTab = false
}) => {
  const [, setSearchParams] = useSearchParams();

  const onClickRow = (id: string) => {
    if (isProductTab) {
      setSearchParams({ order: id, tab: 'orders' });

      return;
    }
    setSearchParams({ order: id });
  };

  const list = useAsyncList<BulkOrdering>({
    async load() {
      return {
        items: orders
      };
    },
    async sort({ items, sortDescriptor }) {
      if (!sortDescriptor.direction) {
        return {
          items: [...orders]
        };
      }

      return {
        items: items.sort((a: any, b: any) => {
          const first = a[sortDescriptor.column];
          const second = b[sortDescriptor.column];

          if (first == null) return 1;
          if (second == null) return -1;

          let cmp: number;

          if (
            typeof first === 'string' &&
            typeof second === 'string' &&
            isValidDate(first) &&
            isValidDate(second)
          ) {
            cmp = new Date(first).getTime() - new Date(second).getTime();
          } else {
            const firstNum = Number(first);
            const secondNum = Number(second);

            if (!isNaN(firstNum) && !isNaN(secondNum)) {
              cmp = (firstNum || 0) - (secondNum || 0);
            } else {
              cmp = String(first).localeCompare(String(second), undefined, {
                sensitivity: 'base'
              });
            }
          }

          if (sortDescriptor.direction === 'descending') {
            cmp *= -1;
          }

          return cmp;
        })
      };
    }
  });

  useEffect(() => {
    list.reload();
  }, [orders]);

  const handleSortChange = (descriptor: SortDescriptor) => {
    if (
      list.sortDescriptor?.column === descriptor.column &&
      list.sortDescriptor?.direction === 'descending'
    ) {
      list.sort({ column: undefined, direction: undefined } as any);
    } else {
      list.sort(descriptor);
    }
  };

  const renderCell = useCallback((item: BulkOrdering, columnKey: Key) => {
    switch (columnKey) {
      case 'name':
        return (
          <Typography variant="body-2" weight="medium">
            {item.name}
          </Typography>
        );

      case 'stage':
        const stageLabels = item.is_cavela_direct
          ? CAVELA_DIRECT_STAGE_LABELS
          : STAGE_LABELS;

        return (
          <Typography variant="body-2">
            {stageLabels[item.stage] || item.stage}
          </Typography>
        );

      case 'quantity':
        return (
          <Typography variant="body-2">{item?.order_quantity ?? 0}</Typography>
        );

      case 'requested_at':
        return (
          <Typography variant="body-2">
            {format(new Date(item?.requested_at), 'MMM d, yyyy')}
          </Typography>
        );
      case 'amount_paid':
        return (
          <Typography variant="body-2">
            {formatMoney(item.total_amount_paid)}
          </Typography>
        );

      case 'supplier':
        return (
          <Typography variant="body-2">{item?.cloak ?? 'None'}</Typography>
        );

      case 'tracking_link':
        return (
          <Typography variant="body-2">
            {item.user_shipments?.length > 0 ? (
              <div className="flex flex-col gap-1">
                {item.user_shipments.map((shipment) => (
                  <div
                    key={shipment.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (shipment.tracking_url) {
                        window.open(shipment.tracking_url, '_blank');
                      }
                    }}
                    className={cn(
                      'flex items-center gap-1',
                      shipment.tracking_url &&
                        'cursor-pointer hover:opacity-50 transition-opacity'
                    )}
                  >
                    <LinkIcon className="size-4 pointer-events-none text-primary" />
                    <Typography className="text-primary text-sm">
                      {shipment.tracking_number || 'Tracking link'}
                    </Typography>
                  </div>
                ))}
              </div>
            ) : (
              'None'
            )}
          </Typography>
        );

      case 'completion':
        const isFinished =
          item.stage?.includes('rejected') || item.stage?.includes('delivered');

        return (
          <div className="flex items-center gap-1">
            <Typography variant="body-2">{getDeliveredDate(item)}</Typography>
            {!isFinished && item.estimated_end_date ? <ExpectedTag /> : null}
          </div>
        );

      default:
        return null;
    }
  }, []);

  return (
    <div>
      <Table
        aria-label="Orders table"
        sortDescriptor={list?.sortDescriptor}
        onSortChange={handleSortChange}
        className={cn(
          'heroui-table',
          orders?.length === 0 && 'pointer-events-none'
        )}
      >
        <TableHeader columns={bulkOrderingsTableColumns}>
          {(column) => (
            <TableColumn
              key={column.key}
              className={cn('px-4', column.className)}
              allowsSorting={column.allowsSorting}
            >
              {column.label}
            </TableColumn>
          )}
        </TableHeader>
        <TableBody
          isLoading={isLoading}
          emptyContent={isLoading ? null : 'No orders found'}
          loadingContent={isLoading ? <Spinner /> : 'none'}
          items={list?.items ?? []}
        >
          {(item) => (
            <TableRow key={item.id} onClick={() => onClickRow(item.id)}>
              {(columnKey) => (
                <TableCell>
                  <div className="truncate">{renderCell(item, columnKey)}</div>
                </TableCell>
              )}
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default OrdersTable;
